
import React from 'react';
import PageTransition from '@/components/layout/PageTransition';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider"; 
import { BellRing, Clock, Languages, Mail, MessageSquare, Smartphone, Volume2, Type } from 'lucide-react';
import { toast } from "sonner";
import { useFontSize } from '@/contexts/FontSizeContext';

const Settings = () => {
  const { fontSize, setFontSize } = useFontSize();
  
  const handleSave = () => {
    toast.success("Settings saved successfully");
  };

  const handleFontSizeChange = (value: number[]) => {
    setFontSize(value[0]);
  };

  return (
    <PageTransition>
      <div className="section-padding">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Account Settings</h1>
          <p className="text-muted-foreground">
            Manage your account settings and preferences
          </p>
        </div>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-4 md:w-auto md:inline-flex mb-6">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
            <TabsTrigger value="appearance">Appearance</TabsTrigger>
            <TabsTrigger value="devices">Devices</TabsTrigger>
          </TabsList>
          
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>
                  Manage your basic account settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Language & Region</h3>
                  
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="language">Language</Label>
                      <select 
                        id="language" 
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                        defaultValue="en"
                      >
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                        <option value="zh">Chinese</option>
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="timezone">Time Zone</Label>
                      <select 
                        id="timezone" 
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                        defaultValue="utc-7"
                      >
                        <option value="utc-12">UTC-12:00</option>
                        <option value="utc-11">UTC-11:00</option>
                        <option value="utc-10">UTC-10:00</option>
                        <option value="utc-9">UTC-09:00</option>
                        <option value="utc-8">UTC-08:00</option>
                        <option value="utc-7">UTC-07:00 (PDT)</option>
                        <option value="utc-6">UTC-06:00 (MDT)</option>
                        <option value="utc-5">UTC-05:00 (EDT)</option>
                        <option value="utc-4">UTC-04:00</option>
                        <option value="utc-3">UTC-03:00</option>
                        <option value="utc-2">UTC-02:00</option>
                        <option value="utc-1">UTC-01:00</option>
                        <option value="utc">UTC±00:00</option>
                        <option value="utc+1">UTC+01:00</option>
                        <option value="utc+2">UTC+02:00</option>
                        <option value="utc+3">UTC+03:00</option>
                        <option value="utc+4">UTC+04:00</option>
                        <option value="utc+5">UTC+05:00</option>
                        <option value="utc+5.5">UTC+05:30</option>
                        <option value="utc+6">UTC+06:00</option>
                        <option value="utc+7">UTC+07:00</option>
                        <option value="utc+8">UTC+08:00</option>
                        <option value="utc+9">UTC+09:00</option>
                        <option value="utc+10">UTC+10:00</option>
                        <option value="utc+11">UTC+11:00</option>
                        <option value="utc+12">UTC+12:00</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center space-x-2">
                      <Languages className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor="autoDetect">Auto-detect language</Label>
                    </div>
                    <Switch id="autoDetect" defaultChecked />
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Date & Time Format</h3>
                  
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="dateFormat">Date Format</Label>
                      <select 
                        id="dateFormat" 
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                        defaultValue="mm-dd-yyyy"
                      >
                        <option value="mm-dd-yyyy">MM/DD/YYYY</option>
                        <option value="dd-mm-yyyy">DD/MM/YYYY</option>
                        <option value="yyyy-mm-dd">YYYY/MM/DD</option>
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="timeFormat">Time Format</Label>
                      <select 
                        id="timeFormat" 
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                        defaultValue="12h"
                      >
                        <option value="12h">12-hour (AM/PM)</option>
                        <option value="24h">24-hour</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor="autoTime">Auto-update time</Label>
                    </div>
                    <Switch id="autoTime" defaultChecked />
                  </div>
                </div>
                
                <div className="pt-4 flex justify-end">
                  <Button onClick={handleSave}>Save Changes</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>
                  Customize how you receive notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Email Notifications</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <Label htmlFor="emailSystem">System alerts</Label>
                      </div>
                      <Switch id="emailSystem" defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <Label htmlFor="emailDevice">Device status changes</Label>
                      </div>
                      <Switch id="emailDevice" defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <Label htmlFor="emailSecurity">Security alerts</Label>
                      </div>
                      <Switch id="emailSecurity" defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <Label htmlFor="emailUpdate">Software updates</Label>
                      </div>
                      <Switch id="emailUpdate" defaultChecked />
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Push Notifications</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <BellRing className="h-4 w-4 text-muted-foreground" />
                        <Label htmlFor="pushSystem">System alerts</Label>
                      </div>
                      <Switch id="pushSystem" defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <BellRing className="h-4 w-4 text-muted-foreground" />
                        <Label htmlFor="pushDevice">Device status changes</Label>
                      </div>
                      <Switch id="pushDevice" defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <BellRing className="h-4 w-4 text-muted-foreground" />
                        <Label htmlFor="pushSecurity">Security alerts</Label>
                      </div>
                      <Switch id="pushSecurity" defaultChecked />
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">SMS Notifications</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        <Label htmlFor="smsSystem">Critical system alerts</Label>
                      </div>
                      <Switch id="smsSystem" />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        <Label htmlFor="smsSecurity">Security breaches</Label>
                      </div>
                      <Switch id="smsSecurity" />
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 flex justify-end">
                  <Button onClick={handleSave}>Save Changes</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="appearance">
            <Card>
              <CardHeader>
                <CardTitle>Appearance Settings</CardTitle>
                <CardDescription>
                  Customize the look and feel of your dashboard
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Font Size</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Type className="h-4 w-4 text-muted-foreground" />
                      <Label className="text-sm">Adjust the application font size ({fontSize}px)</Label>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs">11px</span>
                      <Slider 
                        value={[fontSize]} 
                        min={11} 
                        max={25} 
                        step={1} 
                        onValueChange={handleFontSizeChange} 
                        className="flex-1"
                      />
                      <span className="text-xs">25px</span>
                    </div>
                    <div className="flex justify-center mt-2">
                      <div className="px-4 py-2 border rounded-md" style={{ fontSize: `${fontSize}px` }}>
                        Sample Text
                      </div>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Display</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="density">Density</Label>
                    <select 
                      id="density" 
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      defaultValue="compact"
                    >
                      <option value="comfortable">Comfortable</option>
                      <option value="compact">Compact</option>
                      <option value="dense">Dense</option>
                    </select>
                    <p className="text-sm text-muted-foreground">Controls the spacing between elements</p>
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Accessibility</h3>
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="reducedMotion">Reduced Motion</Label>
                      <p className="text-sm text-muted-foreground">Minimize animations</p>
                    </div>
                    <Switch id="reducedMotion" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="highContrast">High Contrast</Label>
                      <p className="text-sm text-muted-foreground">Increase contrast between elements</p>
                    </div>
                    <Switch id="highContrast" />
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Sound</h3>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Volume2 className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor="soundEffects">Sound Effects</Label>
                    </div>
                    <Switch id="soundEffects" defaultChecked />
                  </div>
                </div>
                
                <div className="pt-4 flex justify-end">
                  <Button onClick={handleSave}>Save Changes</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="devices">
            <Card>
              <CardHeader>
                <CardTitle>Devices</CardTitle>
                <CardDescription>
                  Manage devices connected to your account
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="flex items-center justify-between border-b border-border pb-4 last:border-0 last:pb-0">
                    <div className="flex items-start gap-4">
                      <div className="bg-muted rounded-md p-2">
                        <Smartphone className="h-6 w-6" />
                      </div>
                      
                      <div>
                        <h4 className="font-medium">{index === 0 ? "Current Browser" : `Device ${index}`}</h4>
                        <p className="text-sm text-muted-foreground">{index === 0 ? "Chrome on Windows" : `${index === 1 ? "Firefox on Windows" : "Safari on MacOS"}`}</p>
                        <p className="text-xs text-muted-foreground">Last active: {index === 0 ? "Now" : `${index + 1} days ago`}</p>
                      </div>
                    </div>
                    
                    <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                      {index === 0 ? "This Device" : "Sign Out"}
                    </Button>
                  </div>
                ))}
                
                <div className="pt-4 flex justify-end">
                  <Button onClick={handleSave} variant="outline">Sign Out All Devices</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PageTransition>
  );
};

export default Settings;
